import { useEffect, useRef, useState } from "react";
import {
  Link,
  Navigate,
  Path,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  RyeMainContentPanel,
  RyeMainContentPanelTrailingActions,
} from "../rye/RyeMainContentPanel";
import {
  RyeMainContentSidebar,
  RyeSidebarNavItemContent,
  ryeSidebarNavItemClasses,
} from "../rye/RyeMainContentSidebar";
import { buildClasses } from "../utils/buildClasses";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { RyeButton } from "../rye/RyeButton";
import { useRyeModal } from "../rye/RyeModal";
import {
  RYE_MODAL_EXIT_MS,
  RyeModalPhase,
  RyeModalBackdrop,
  ryeModalPanelClasses,
} from "../rye/RyeModalMotion";
import { GeneralSettings } from "./GeneralSettings";
import { BillingSettings } from "./BillingSettings";
import { ProfileSettings } from "./ProfileSettings";
import { SupportSettings } from "./SupportSettings";
import { AgentsSettings } from "./AgentsSettings";
import { DevSettings } from "./DevSettings";
import { getAuth, signOut } from "firebase/auth";
import { useIsDev } from "../utils/customHooks";
import { keyCodes } from "../utils/KeyCodes";
import { AccountsScreen } from "../accounts_screen/AccountsScreen";
import { CategoriesScreen } from "../categories_screen/CategoriesScreen";
import { TagsScreen } from "../tags_screen/TagsScreen";
import { RulesScreen } from "../rules_screen/RulesScreen";
import {
  getSettingsTabParam,
  SETTINGS_DEFINITIONS,
  SettingsTab,
  wasSettingsOpenedInApp,
  withSettingsTab,
} from "./settingsDefinitions";

// How long closing waits for a step back in history to take effect before
// removing Settings from the URL itself.
const BACK_FALLBACK_MS = 500;

/**
 * Settings, shown over the current screen while its URL has ?settings=<tab>.
 *
 * Dialogs opened from inside Settings (Add Account, etc.) still go through
 * RyeModal, whose layer sits above this one, so they appear on top of it.
 */
export function SettingsModal(): JSX.Element {
  const isDev = useIsDev();
  const location = useLocation();
  const navigate = useNavigate();
  const { isOpen: isDialogOpen } = useRyeModal();
  const frameRef = useRef<HTMLDivElement>(null);
  const backdropPressed = useRef(false);
  // Animates like RyeModal. Closing plays out before leaving the URL.
  const [phase, setPhase] = useState<RyeModalPhase>("entering");
  const isClosing = useRef(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestLocation = useRef(location);
  latestLocation.current = location;
  const tabParam = getSettingsTabParam(location.search);
  const visibleTabs = SETTINGS_DEFINITIONS.filter(
    (tab) => !("devOnly" in tab && tab.devOnly) || isDev,
  );
  const activeTab =
    visibleTabs.find((tab) => tab.slug === tabParam) ?? visibleTabs[0];
  const isCanonicalTab = tabParam === activeTab.slug;

  function removeSettingsFromUrl() {
    const current = latestLocation.current;
    navigate(withSettingsTab(current, null), { replace: true });
  }

  function close() {
    if (isClosing.current) return;
    isClosing.current = true;
    setPhase("closing");
    closeTimeout.current = setTimeout(() => {
      if (!wasSettingsOpenedInApp(location)) {
        removeSettingsFromUrl();
        return;
      }
      // Opening Settings pushed one history entry and tab switches replace it,
      // so stepping back normally returns to the screen underneath, keeping
      // Back and Forward natural. If it doesn't leave Settings, the effect
      // below or this fallback removes Settings from the URL instead.
      const keyBeforeBack = location.key;
      navigate(-1);
      closeTimeout.current = setTimeout(() => {
        if (latestLocation.current.key === keyBeforeBack) {
          // The step back did nothing, e.g. there was no entry before this one.
          removeSettingsFromUrl();
        }
      }, BACK_FALLBACK_MS);
    }, RYE_MODAL_EXIT_MS);
  }

  // Stepping back landed on another Settings entry, so Settings is still in
  // the URL. Finish closing by removing it.
  useEffect(() => {
    if (isClosing.current) removeSettingsFromUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  useEffect(() => {
    if (!isCanonicalTab) return;
    const frame = requestAnimationFrame(() => setPhase("open"));
    return () => cancelAnimationFrame(frame);
  }, [isCanonicalTab]);

  useEffect(() => {
    if (!isCanonicalTab) return;
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.code !== keyCodes.ESC ||
        event.repeat ||
        // An open dialog, dropdown, or field inside Settings gets Esc first.
        isDialogOpen ||
        event.defaultPrevented ||
        isEditableElement(event.target)
      ) {
        return;
      }
      close();
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  useEffect(() => {
    // A dialog opened together with Settings (e.g. Add Account from Home)
    // sits on top and keeps focus.
    if (!isCanonicalTab || isDialogOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    frameRef.current?.focus();
    return () => previouslyFocused?.focus?.();
    // Only on opening; a dialog opened later doesn't move focus back here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCanonicalTab]);

  // Leaving another way (e.g. Back) mid-close must not also navigate again.
  useEffect(() => {
    return () => {
      if (closeTimeout.current !== null) clearTimeout(closeTimeout.current);
    };
  }, []);

  // Keep ?settings= and invalid tabs working. Replacing also prevents these
  // URLs from adding a history entry.
  if (!isCanonicalTab) {
    return (
      <Navigate
        to={withSettingsTab(location, activeTab.slug)}
        state={location.state}
        replace
      />
    );
  }

  // The moved data screens render their own panel because they have header
  // actions; the remaining settings tabs share a plain titled panel.
  const tabContent: Record<SettingsTab, JSX.Element> = {
    accounts: <AccountsScreen />,
    categories: <CategoriesScreen />,
    tags: <TagsScreen />,
    rules: <RulesScreen />,
    preferences: (
      <SettingsPanel title="Preferences" content={<GeneralSettings />} />
    ),
    billing: <SettingsPanel title="Billing" content={<BillingSettings />} />,
    profile: <SettingsPanel title="Profile" content={<ProfileSettings />} />,
    support: <SettingsPanel title="Support" content={<SupportSettings />} />,
    agents: <SettingsPanel title="Agents" content={<AgentsSettings />} />,
    dev: <SettingsPanel title="Dev" content={<DevSettings />} />,
  };

  return (
    <div
      className={buildClasses(
        "fixed",
        "inset-0",
        // Above the app, below RyeModal (z-[1000000000]) and popovers.
        "z-[900000000]",
        "flex",
        "items-center",
        "justify-center",
        "p-8",
        // Once closing starts, let clicks reach the screen underneath.
        { if: phase === "closing", then: "pointer-events-none" },
      )}
      // Only a press that starts and ends on the backdrop closes Settings, so
      // a text selection or drag that ends outside the frame doesn't.
      onMouseDown={(e) => {
        backdropPressed.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (backdropPressed.current && e.target === e.currentTarget) {
          close();
        }
        backdropPressed.current = false;
      }}
    >
      <RyeModalBackdrop phase={phase} dimClass="bg-on-surface-900/20" />
      <div
        ref={frameRef}
        role="dialog"
        aria-modal="true"
        aria-label="Settings"
        tabIndex={-1}
        className={buildClasses(
          "w-full",
          "h-full",
          "max-w-[96rem]",
          "max-h-[64rem]",
          "rounded-[0.8rem]",
          "outline-none",
          "shadow-2xl",
          ryeModalPanelClasses(phase),
        )}
      >
        <RyeScreenWrapper>
          <div className={buildClasses("flex", "w-full", "h-full", "min-h-0")}>
            <RyeMainContentSidebar title="Settings" joinHeader embedded>
              <div className={buildClasses("flex", "flex-col", "gap-1")}>
                {visibleTabs.map((tab, index) => (
                  <div key={tab.slug}>
                    {index > 0 &&
                      tab.section !== visibleTabs[index - 1].section && (
                        <SectionDivider />
                      )}
                    <TabLink
                      text={tab.label}
                      iconName={tab.icon}
                      to={withSettingsTab(location, tab.slug)}
                      isSelected={tab.slug === activeTab.slug}
                    />
                  </div>
                ))}
                <div>
                  <SectionDivider />
                  <button
                    type="button"
                    aria-label="Sign out"
                    className={buildClasses(
                      ryeSidebarNavItemClasses(false),
                      "w-full",
                    )}
                    onClick={() => void signOut(getAuth())}
                  >
                    <RyeSidebarNavItemContent
                      icon="logout"
                      label="Sign out"
                      isSelected={false}
                    />
                  </button>
                </div>
              </div>
            </RyeMainContentSidebar>
            {/* Each tab draws its own header, so the close button is handed to
                it to sit after the tab's own actions, top right. */}
            <RyeMainContentPanelTrailingActions.Provider
              value={
                <RyeButton
                  icon="close"
                  size="sm"
                  variant="transparent"
                  vibe="subdued"
                  tooltip="Close settings"
                  onClick={close}
                />
              }
            >
              {tabContent[activeTab.slug]}
            </RyeMainContentPanelTrailingActions.Provider>
          </div>
        </RyeScreenWrapper>
      </div>
    </div>
  );
}

function isEditableElement(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
  );
}

function SectionDivider(): JSX.Element {
  return (
    <div
      className={buildClasses(
        "border-t",
        "border-on-surface-200",
        "mx-3",
        "mt-2",
        "mb-4",
      )}
    />
  );
}

function SettingsPanel({
  title,
  content,
}: {
  title: string;
  content: JSX.Element;
}): JSX.Element {
  return (
    <RyeMainContentPanel roundedTl={false} roundedBl={false} title={title}>
      {content}
    </RyeMainContentPanel>
  );
}

function TabLink({
  text,
  iconName,
  to,
  isSelected,
}: {
  text: string;
  iconName: string;
  to: Path;
  isSelected: boolean;
}): JSX.Element {
  const location = useLocation();
  return (
    <Link
      to={to}
      // Replace so Back closes Settings rather than stepping through tabs,
      // and keep the state that lets closing step back.
      replace
      state={location.state}
      aria-label={text}
      aria-current={isSelected ? "page" : undefined}
      className={ryeSidebarNavItemClasses(isSelected)}
    >
      <RyeSidebarNavItemContent
        icon={iconName}
        label={text}
        isSelected={isSelected}
      />
    </Link>
  );
}
